import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import Input from "@/app/components/ui/Inputs/Input";
import { useUseCallDispositationAPI } from "@/app/hooks/api_hooks/ameyo/useUseCallDispositationAPI";
import { useUserCallDetailsAPI } from "@/app/hooks/api_hooks/ameyo/useUserCallDetailsAPI";
import { useSearchCustomerAPI } from "@/app/hooks/api_hooks/customer_profile/useSearchCustomerAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useUtils } from "@/app/hooks/useUtils";
import { IExotelInformationData, ISearchCustomerResponseData } from "@/api_framework/api_modals/Users";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import {
    clearExotelInformation,
    ICustomerProfileState,
    setCustomerAndPetId,
    setExotelInformation,
} from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import gvaccinationIcon from "@/assets/images/gvaccinationIcon.svg";
import rvaccinationIcon from "@/assets/images/rvaccinationIcon.svg";
import phoneicon from "@/assets/images/phoneicon.svg";
import clinicIcon from "@/assets/images/clinic_Icon.svg";
import dog from "@/assets/images/dog.png";
import cat from "@/assets/images/cat.png";
import { ChevronDown, ChevronUp } from "lucide-react";
import { HiOutlineLink } from "react-icons/hi";
import SmartQCDeeplinkModal from "@/app/components/routes/modals/SmartQCDeeplinkModal";
import ExotelTalkingPoints from "./ExotelTalkingPoints";

type TVaccinationBadgeVariant =
    | "missing_data"
    | "up_to_date"
    | "upcoming"
    | "overdue"
    | "highcare"
    | "vaccinated";

const getVaccinationBadgeClass = (variant: TVaccinationBadgeVariant): string => {
    const baseClasses = "flex items-center gap-[2px] rounded-[44px] border-[0.5px] py-[2px] px-[6px] text-[10px] font-medium leading-[14px] cursor-pointer";
    const variantStyles: Record<TVaccinationBadgeVariant, string> = {
        missing_data: "border-[#D1D5DB] bg-[linear-gradient(111deg,#F3F4F6_29.47%,#E5E7EB_106.2%)] shadow-[inset_0_-2px_4px_rgba(209,213,219,0.25),inset_0_4px_4px_rgba(255,255,255,0.25)] text-[#4B5563]",
        up_to_date: "border-[#C3FFBD] bg-[linear-gradient(111deg,#E6FFEA_29.47%,#C7FFE4_106.2%)] shadow-[inset_0_-2px_4px_rgba(149,255,199,0.25),inset_0_4px_4px_rgba(255,255,255,0.25)] text-[#149463]",
        upcoming: "border-[#FFE1A8] bg-[linear-gradient(111deg,#FFF8E3_29.47%,#FFECC0_106.2%)] shadow-[inset_0_-2px_4px_rgba(255,216,128,0.25),inset_0_4px_4px_rgba(255,255,255,0.25)] text-[#B7791F]",
        overdue: "border-transparent bg-[linear-gradient(135deg,#FFE6EF_0%,#FFC7DB_100%)_padding-box,linear-gradient(180deg,#FFBDC8_0%,#EEAAC2_100%)_border-box] shadow-[inset_0_-2px_4px_rgba(255,149,149,0.25),inset_0_4px_4px_rgba(255,255,255,0.25)] text-[#FF0029] text-center",
        highcare: "border-transparent bg-[linear-gradient(135deg,#FFE6EF_0%,#FFC7DB_100%)_padding-box,linear-gradient(180deg,#FFBDC8_0%,#EEAAC2_100%)_border-box] shadow-[inset_0_-2px_4px_rgba(255,149,149,0.25),inset_0_4px_4px_rgba(255,255,255,0.25)] text-[#FF0029] text-center",
        vaccinated: "border-transparent bg-[linear-gradient(135deg,#E6FFEA_0%,#C7FFE4_100%)_padding-box,linear-gradient(180deg,#C3FFBD_0%,#AAEEBC_100%)_border-box] shadow-[inset_0_-2px_4px_rgba(149,255,199,0.25),inset_0_4px_4px_rgba(255,255,255,0.25)] text-[#149463]",
    };

    return `${baseClasses} ${variantStyles[variant]}`;
};


type TDispositionL2Option = {
    l2_code: string;
    l2_label: string;
};

type TDispositionL1Category = {
    l1_code: string;
    l1_label: string;
    l2_options: TDispositionL2Option[];
};

type TDispositionGroup = {
    type: string;
    l1_categories: TDispositionL1Category[];
};

type TPetData = {
    id: string;
    name: string;
    species_id: string;
    vaccination_status: string;
    high_risk: boolean;
};

type TUserCallDetails = {
    city: string;
    initiated_at?: number | string;
    customer?: {
        id?: string;
        name?: string;
        phone?: string;
        persona?: string;
        customer_type?: string;
    };
    pets?: TPetData[];
    disposition_data?: TDispositionGroup[];
    crt_id?: string;
    direction?: string;
    queue_name?: string;
};

type TDispositionForm = {
    disposition_l1_code: string;
    disposition_l2_code: string;
    description: string;
    lookup_mobile?: string;
};

const normalizeRoleUserId = (value: unknown): string => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
        const normalized = value as { user_id?: string; id?: string };
        return normalized.user_id ?? normalized.id ?? "";
    }
    return "";
};

const toTitleCase = (value?: string) => {
    if (!value) return "";
    return value
        .trim()
        .split(/[\s_]+/)
        .filter(Boolean)
        .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`)
        .join(" ");
};

const getVaccinationBadge = (vaccinationStatus?: string): { label: string; variant: TVaccinationBadgeVariant } => {
    const normalizedStatus = (vaccinationStatus ?? "").trim().toLowerCase();

    if (!normalizedStatus || normalizedStatus === "missing_data" || normalizedStatus.includes("missing")) {
        return { label: "Vaccination Missing data", variant: "missing_data" };
    }

    if (normalizedStatus === "all_done" || normalizedStatus === "up_to_date") {
        return { label: "Vaccination Up To Date", variant: "up_to_date" };
    }

    if (normalizedStatus === "upcoming") {
        return { label: "Vaccination Upcoming", variant: "upcoming" };
    }

    if (normalizedStatus === "over_due" || normalizedStatus === "overdue") {
        return { label: "Vaccination Overdue", variant: "overdue" };
    }

    const isVaccinated =
        normalizedStatus.includes("vaccinated") &&
        !normalizedStatus.includes("not vaccinated") &&
        !normalizedStatus.includes("unvaccinated");

    if (isVaccinated) {
        return { label: "Vaccinated", variant: "up_to_date" };
    }

    return { label: toTitleCase(vaccinationStatus), variant: "missing_data" };
};

const VeticExotelIntegration = () => {
    const dispatch = useAppDispatch();
    const authUser = useAppSelector((state) => state.auth?.user);
    const vmsUserId = useAppSelector((state) => state.auth?.vms_user_id);
    const navigate = useNavigate();
    const location = useLocation();

    const { getRouteKey } = useRouter();
    const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
    const { exotelInformation: exotelInfoFromStore } = routeState.state as ICustomerProfileState;

    const queryParams = new URLSearchParams(location.search);
    const crtIdFromURL = queryParams.get("CRT") ?? queryParams.get("CRT_ID") ?? "";
    const phoneNumberFromURL = queryParams.get("phone_number") ?? "";
    const customerIdFromQuery = queryParams.get("customer_id") ?? "";
    const petIdFromQuery = queryParams.get("pet_id") ?? "";
    const exotelSessionInfo = useMemo(() => {
        try {
            const currentData = sessionStorage.getItem("exotel_information_current");
            if (currentData) {
                const parsedCurrentData = JSON.parse(currentData) as IExotelInformationData;
                if (parsedCurrentData?.CRT) return parsedCurrentData;
            }
            if (exotelInfoFromStore?.CRT) {
                return exotelInfoFromStore;
            }
            if (!crtIdFromURL) return null;
            const crtSpecificData = sessionStorage.getItem(`exotel_information:${crtIdFromURL}`);
            if (!crtSpecificData) return null;
            return JSON.parse(crtSpecificData) as IExotelInformationData;
        } catch {
            return exotelInfoFromStore?.CRT ? exotelInfoFromStore : null;
        }
    }, [crtIdFromURL, exotelInfoFromStore]);

    const sessionCustomerId = exotelSessionInfo?.customer_pet_data?.customer_id ?? "";
    const sessionPetId = exotelSessionInfo?.customer_pet_data?.pet_id ?? "";
    const sessionRawPhoneNumber = exotelSessionInfo?.rawPhoneNumber ?? "";
    const resolvedCustomerId = sessionCustomerId || customerIdFromQuery || "";
    const resolvedPetId = sessionPetId || petIdFromQuery || "";
    const [isPopupLoading, setIsPopupLoading] = useState(false);
    const [isDisposingCall, setIsDisposingCall] = useState(false);
    const [showAllPets, setShowAllPets] = useState(false);
    const [showExistingMemberLookup, setShowExistingMemberLookup] = useState(false);
    const [lookupMobile, setLookupMobile] = useState("");
    const [lookupError, setLookupError] = useState("");
    const [matchedCustomer, setMatchedCustomer] = useState<ISearchCustomerResponseData | null>(null);
    const [openSmartQCDeeplink, setOpenSmartQCDeeplink] = useState(false);
    const lastFetchedPhoneRef = useRef<string>("");
    const lastEffectRunKeyRef = useRef<string>("");
    const lastRequestKeyRef = useRef<string>("");

    const { execute: fetchCallDetails, userCallDetails } = useUserCallDetailsAPI();
    const { execute: searchCustomer } = useSearchCustomerAPI();
    const { execute: updateCallDetails } = useUseCallDispositationAPI();
    const { veticDateFormat, maskMobile } = useUtils();

    const formHook = useForm<TDispositionForm>({
        mode: "onChange",
        defaultValues: {
            disposition_l1_code: "",
            disposition_l2_code: "",
            description: "",
        },
    });

    const selectedL1Code = formHook.watch("disposition_l1_code");
    const selectedL2Code = formHook.watch("disposition_l2_code");
    const descriptionValue = formHook.watch("description");

    useEffect(() => {
        const effectRunKey = `${location.pathname}|${location.search}|${resolvedCustomerId || ""}|${resolvedPetId || ""}`;
        if (lastEffectRunKeyRef.current === effectRunKey) return;
        lastEffectRunKeyRef.current = effectRunKey;

        const loadCallDetails = async () => {
            setIsPopupLoading(true);
            try {
                if (!lastFetchedPhoneRef.current) {
                    lastFetchedPhoneRef.current = phoneNumberFromURL || sessionRawPhoneNumber || "";
                }
                const phoneFallback = phoneNumberFromURL || sessionRawPhoneNumber || lastFetchedPhoneRef.current;
                const shouldUsePhoneFallback = !resolvedCustomerId && Boolean(phoneFallback);
                const initialPhoneParam = shouldUsePhoneFallback ? phoneFallback : undefined;
                const resolvedCRTParam = crtIdFromURL || exotelSessionInfo?.CRT || undefined;
                const requestKey = `${resolvedCustomerId || ""}|${initialPhoneParam || ""}|${resolvedCRTParam || ""}`;
                if (lastRequestKeyRef.current === requestKey) return;
                lastRequestKeyRef.current = requestKey;
                const response = await fetchCallDetails(resolvedCustomerId ?? "", initialPhoneParam, resolvedCRTParam);
                const resolvedPhoneFromResponse = response?.data?.customer?.phone;
                const resolvedCRTFromResponse = response?.data?.crt_id;
                const rawDidFromResponse = response?.data?.did_number;
                const resolvedDidFromResponse =
                    rawDidFromResponse === null || rawDidFromResponse === undefined
                        ? null
                        : String(rawDidFromResponse).trim() || null;

                if (resolvedCustomerId) {
                    const nextCRT = resolvedCRTFromResponse || exotelSessionInfo?.CRT || crtIdFromURL || null;
                    const nextPhone = resolvedPhoneFromResponse || phoneFallback || null;
                    const nextDidNumber = exotelSessionInfo?.did_number || resolvedDidFromResponse || null;
                    const shouldUpdateExotelSession =
                        (exotelSessionInfo?.customer_pet_data?.customer_id ?? null) !== (resolvedCustomerId || null) ||
                        (exotelSessionInfo?.customer_pet_data?.pet_id ?? null) !== (resolvedPetId || null) ||
                        (exotelSessionInfo?.rawPhoneNumber ?? null) !== nextPhone ||
                        (exotelSessionInfo?.did_number ?? null) !== nextDidNumber ||
                        (exotelSessionInfo?.CRT ?? null) !== nextCRT;

                    if (!shouldUpdateExotelSession) return;

                    const exotelDataForSession: IExotelInformationData = {
                        event: "incoming_call",
                        customer_pet_data: {
                            customer_id: resolvedCustomerId,
                            pet_id: resolvedPetId || null,
                        },
                        rawPhoneNumber: nextPhone,
                        did_number: nextDidNumber,
                        CRT: nextCRT,
                    };

                    dispatch(setExotelInformation(exotelDataForSession));
                    const storageKey = exotelDataForSession.CRT
                        ? `exotel_information:${exotelDataForSession.CRT}`
                        : "exotel_information";
                    sessionStorage.setItem(storageKey, JSON.stringify(exotelDataForSession));
                    sessionStorage.setItem("exotel_information_current", JSON.stringify(exotelDataForSession));
                }

                if (resolvedCustomerId && resolvedPhoneFromResponse) {
                    lastFetchedPhoneRef.current = resolvedPhoneFromResponse;
                }
            } finally {
                setIsPopupLoading(false);
            }
        };

        loadCallDetails();
    }, [
        location.pathname,
        location.search,
        resolvedCustomerId,
        resolvedPetId,
        phoneNumberFromURL,
        sessionRawPhoneNumber,
        fetchCallDetails,
        dispatch,
        exotelSessionInfo,
        crtIdFromURL,
    ]);

    const callDetails = useMemo(() => {
        if (!userCallDetails || Array.isArray(userCallDetails)) return null;
        return userCallDetails as TUserCallDetails;
    }, [userCallDetails]);

    // did_number sources: websocket payload (session) first, then call-details API.
    const resolvedDidNumber = useMemo(() => {
        const normalizeDid = (value: unknown) => {
            if (value === null || value === undefined) return null;
            return String(value).trim() || null;
        };
        return normalizeDid(exotelSessionInfo?.did_number) ?? normalizeDid((callDetails as any)?.did_number);
    }, [exotelSessionInfo, callDetails]);

    const directionBasedDisposition = useMemo(() => {
        const dispositionGroups = callDetails?.disposition_data ?? [];
        if (dispositionGroups.length === 0) return null;

        const currentDirection = (callDetails?.direction ?? "").toUpperCase();
        const matchedGroup = dispositionGroups.find(
            (group) => (group?.type ?? "").toUpperCase() === currentDirection,
        );

        return matchedGroup ?? dispositionGroups[0];
    }, [callDetails]);

    const l1Categories = directionBasedDisposition?.l1_categories ?? [];
    const dispositionL1Data = useSelectMenuReducer(l1Categories, "l1_label", "l1_code");

    const selectedL1Category = useMemo(
        () => l1Categories.find((category) => category.l1_code === selectedL1Code),
        [l1Categories, selectedL1Code],
    );

    const dispositionL2Data = useSelectMenuReducer(selectedL1Category?.l2_options ?? [], "l2_label", "l2_code");

    useEffect(() => {
        if (!selectedL2Code) return;
        const hasSelectedL2 = (selectedL1Category?.l2_options ?? []).some((option) => option.l2_code === selectedL2Code);
        if (!hasSelectedL2) {
            formHook.setValue("disposition_l2_code", "");
        }
    }, [selectedL1Category, selectedL2Code, formHook]);

    const selectedL1Item = dispositionL1Data.find((item) => item.id === selectedL1Code);
    const selectedL2Item = dispositionL2Data.find((item) => item.id === selectedL2Code);
    const resolvedCRTId = callDetails?.crt_id ?? crtIdFromURL ?? exotelSessionInfo?.CRT ?? "";

    const personaLabel = callDetails?.customer?.persona;
    const resolvedDirectionLabel = toTitleCase(callDetails?.direction);

    const initiatedAtLabel = useMemo(() => {
        const formatted = veticDateFormat(callDetails?.initiated_at);
        if (!formatted) return "";

        const match = formatted.match(/^(\d{2}) ([A-Z]{3}) (\d{2}) (\d{1,2}:\d{2} [AP]M)$/);
        if (!match) return formatted;

        const [, day, month, shortYear, time] = match;
        const monthTitleCase = `${month.charAt(0)}${month.slice(1).toLowerCase()}`;
        return `${time} - ${day} ${monthTitleCase}, 20${shortYear}`;
    }, [callDetails?.initiated_at, veticDateFormat]);

    const handleDisposeCall = async () => {
        if (!resolvedCRTId || !selectedL1Code || !selectedL2Code) return;

        const selectedL2Option = (selectedL1Category?.l2_options ?? []).find((item) => item.l2_code === selectedL2Code);

        const payload = {
            crt_id: resolvedCRTId,
            customer_id: callDetails.customer?.id ?? resolvedCustomerId,
            pet_id: resolvedPetId,
            direction: callDetails.direction ?? "",
            l1_code: selectedL1Code,
            l1_label: selectedL1Category?.l1_label ?? "",
            l2_code: selectedL2Code,
            l2_label: selectedL2Option?.l2_label ?? "",
            description: formHook.getValues("description"),
            agent_id: authUser?.email ?? "",
            user_id: normalizeRoleUserId(vmsUserId),
        };

        setIsDisposingCall(true);
        try {
            const response: any = await updateCallDetails(payload as any);

            if (!response || response?.status === false) return;

            if (resolvedCRTId) {
                sessionStorage.removeItem(`exotel_information:${resolvedCRTId}`);
            }
            if (crtIdFromURL) {
                sessionStorage.removeItem(`exotel_information:${crtIdFromURL}`);
            }
            sessionStorage.removeItem("exotel_information_current");
            dispatch(clearExotelInformation());

            const nextParams = new URLSearchParams(location.search);
            nextParams.delete("CRT");
            nextParams.delete("CRT_ID");
            nextParams.delete("isExotel");
            nextParams.delete("crt");
            nextParams.delete("crt_id");
            nextParams.delete("isexotel");
            const nextSearch = nextParams.toString();
            window.dispatchEvent(new Event("vetic:close-exotel-panel"));
            navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`, { replace: true });
        } finally {
            setIsDisposingCall(false);
        }
    };

    const pets = callDetails?.pets ?? [];
    const displayPhone =
        callDetails?.customer?.phone || phoneNumberFromURL || sessionRawPhoneNumber || lastFetchedPhoneRef.current || "";
    const maskedDisplayPhone = displayPhone ? maskMobile(displayPhone) : null;
    const hasMorePets = pets.length > 3;
    const visiblePets = showAllPets ? pets : pets.slice(0, 3);
    const isDescriptionMissing = !descriptionValue?.trim();
    const isDisposeDisabled = !resolvedCRTId || !selectedL1Code || !selectedL2Code || isDescriptionMissing;
    const customerIdForProfile = callDetails?.customer?.id ?? resolvedCustomerId;
    const customerProfileUrl = customerIdForProfile
        ? resolvedPetId
            ? `${getRouteKey("APPOINTMENTS_TAB", "url")}/${customerIdForProfile}/${resolvedPetId}`
            : `${getRouteKey("APPOINTMENTS_TAB", "url")}/${customerIdForProfile}`
        : "";
    const customerNameLabel = callDetails?.customer?.name ?? "Unknown Customer";
    const isUnknownCustomer = !customerIdForProfile || customerNameLabel === "Unknown Customer";
    const resolvedActiveCRT = resolvedCRTId || exotelSessionInfo?.CRT || crtIdFromURL || "";
    const matchedCustomerId = matchedCustomer?.customer_details?.user_id ?? "";
    const matchedPetId = matchedCustomer?.pet_details?.pet_id ?? "";
    const matchedCustomerProfileUrl = matchedCustomerId
        ? matchedPetId
            ? `${getRouteKey("APPOINTMENTS_TAB", "url")}/${matchedCustomerId}/${matchedPetId}`
            : `${getRouteKey("APPOINTMENTS_TAB", "url")}/${matchedCustomerId}`
        : "";
    const createCustomerParams = new URLSearchParams();
    createCustomerParams.set("isExotel", "true");
    if (resolvedActiveCRT) {
        createCustomerParams.set("CRT", resolvedActiveCRT);
    }
    if (displayPhone) {
        createCustomerParams.set("phone_number", displayPhone);
    }
    const createCustomerUrl = `${getRouteKey("ADD_CUSTOMER", "url")}?${createCustomerParams.toString()}`;

    const handleCustomerProfileRedirect = () => {
        if (!customerProfileUrl) return;
        window.open(customerProfileUrl, "_self");
    };

    const handleUnknownCustomerRedirect = () => {
        window.open(createCustomerUrl, "_self");
    };
    const handleLookupExistingCustomer = async () => {
        const normalizedMobile = lookupMobile.replace(/\D/g, "");
        if (normalizedMobile.length !== 10) {
            setLookupError("Please enter a valid 10-digit number");
            return;
        }

        setLookupError("");
        const response = await searchCustomer(normalizedMobile, "0");
        const firstMatchedCustomer = (response ?? [])[0] as ISearchCustomerResponseData | undefined;
        if (!firstMatchedCustomer?.customer_details?.user_id) {
            setMatchedCustomer(null);
            setLookupError("No existing customer found for this mobile number");
            return;
        }

        setMatchedCustomer(firstMatchedCustomer);
        const nextCustomerId = firstMatchedCustomer.customer_details.user_id;
        const nextPetId = firstMatchedCustomer.pet_details?.pet_id ?? null;
        dispatch(
            setCustomerAndPetId({
                customerId: nextCustomerId,
                petId: nextPetId ?? "",
            }),
        );
        const nextMatchedCustomerProfileUrl = nextPetId
            ? `${getRouteKey("APPOINTMENTS_TAB", "url")}/${nextCustomerId}/${nextPetId}`
            : `${getRouteKey("APPOINTMENTS_TAB", "url")}/${nextCustomerId}`;
        const nextExotelSession: IExotelInformationData = {
            event: "incoming_call",
            customer_pet_data: {
                customer_id: nextCustomerId,
                pet_id: nextPetId,
            },
            rawPhoneNumber: normalizedMobile,
            did_number: resolvedDidNumber,
            CRT: resolvedActiveCRT || null,
        };
        dispatch(setExotelInformation(nextExotelSession));
        if (nextExotelSession.CRT) {
            sessionStorage.setItem(`exotel_information:${nextExotelSession.CRT}`, JSON.stringify(nextExotelSession));
        }
        sessionStorage.setItem("exotel_information_current", JSON.stringify(nextExotelSession));
        const nextParams = new URLSearchParams();
        nextParams.set("isExotel", "true");
        if (resolvedActiveCRT) {
            nextParams.set("CRT", resolvedActiveCRT);
        }
        if (normalizedMobile) {
            nextParams.set("phone_number", normalizedMobile);
        }

        navigate(`${nextMatchedCustomerProfileUrl}?${nextParams.toString()}`, {
            replace: true,
        });
    };


    return (
        <div className="h-full bg-white overflow-y-auto show-scrollbar">
            <div className="sticky top-0 z-10 bg-[#F9F9F8] pt-[12px] px-[12px] rounded-b-[12px] border-b border-[rgba(226,226,226,1)]">
                <div className=" flex items-start justify-between gap-2  bg-[#F9F9F8] pb-3">
                    <div>
                        <div className="flex items-center">
                            <h2 className="text-sm font-semibold text-gray-900">
                                {isUnknownCustomer ? (
                                    <button
                                        type="button"
                                        onClick={handleUnknownCustomerRedirect}
                                        className="text-blue-600 hover:underline"
                                    >
                                        {customerNameLabel}
                                    </button>
                                ) : customerProfileUrl ? (
                                    <button
                                        type="button"
                                        onClick={handleCustomerProfileRedirect}
                                        className="text-blue-600 hover:underline"
                                    >
                                        {customerNameLabel}
                                    </button>
                                ) : (
                                    customerNameLabel
                                )}
                            </h2>
                        </div>
                        <p className="text-xs text-gray-500">{maskedDisplayPhone ?? "--"}</p>
                        {showExistingMemberLookup && isUnknownCustomer && (
                            <div className="mt-2">
                                <div className="flex items-center gap-2">
                                    <Input
                                        register={formHook.register("lookup_mobile", {
                                            required: true,
                                        })}
                                        errorMessages={[{ type: "required", message: "Mobile number is required" }]}
                                        className="h-[34px] w-[200px] flex-1 text-xs rounded-md border-[rgba(226,226,226,1)]"
                                        fieldError={formHook.formState.errors.lookup_mobile}
                                        placeholder="Enter mobile number"
                                        value={lookupMobile}
                                        onChange={(event) => {
                                            const cleanedInput = event?.target?.value?.replace(/\D/g, "").slice(0, 10);
                                            setLookupMobile(cleanedInput);
                                            formHook.clearErrors("lookup_mobile");
                                            if (lookupError) setLookupError("");
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleLookupExistingCustomer}
                                        className="h-[34px] rounded-[6px] bg-black px-3 text-xs font-medium text-white truncate"
                                    >
                                        Search Customer
                                    </button>
                                </div>
                                {lookupError && <p className="mt-1 text-[10px] text-red-500">{lookupError}</p>}
                                {matchedCustomer && (
                                    <div className="mt-2 rounded-[6px] bg-white p-2">
                                        <p className="text-xs font-medium text-gray-900">
                                            {matchedCustomer.customer_details.name}
                                        </p>
                                        <p className="text-[10px] text-gray-600">
                                            {maskMobile(matchedCustomer.customer_details.mobile)}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {isUnknownCustomer ? (
                            <button
                                type="button"
                                onClick={() => {
                                    setShowExistingMemberLookup((prev) => !prev);
                                    setLookupError("");
                                    if (!lookupMobile) {
                                        setLookupMobile((displayPhone ?? "").replace(/\D/g, "").slice(-10));
                                    }
                                }}
                                className="inline-flex h-[20px] items-center rounded-[44px] border border-[rgba(226,226,226,1)] px-[8px] text-[10px] font-medium leading-[14px] text-blue-600 hover:underline"
                            >
                                Already a User?
                            </button>
                        ) : (
                            !!personaLabel && (
                                <span className="inline-flex h-[20px] items-center rounded-[44px] border border-[rgba(226,226,226,1)] px-[8px] text-[10px] font-medium leading-[14px] text-gray-600">
                                    {personaLabel}
                                </span>
                            )
                        )}
                    </div>
                </div></div>
            <div>
                <div className="bg-[#F9F9F8] pt-[12px] px-[12px] rounded-b-[12px] border-b border-[rgba(226,226,226,1)]">
                    <div className="flex items-start justify-between gap-2 border-b border-[rgba(226,226,226,1)] pb-[12px] w-full">
                        <div>
                            <div className="flex items-center">
                                <h2 className="text-sm font-semibold text-gray-900">
                                    {isUnknownCustomer ? (
                                        <button
                                            type="button"
                                            onClick={handleUnknownCustomerRedirect}
                                            className="text-blue-600 hover:underline"
                                        >
                                            {customerNameLabel}
                                        </button>
                                    ) : customerProfileUrl ? (
                                        <button
                                            type="button"
                                            onClick={handleCustomerProfileRedirect}
                                            className="text-blue-600 hover:underline"
                                        >
                                            {customerNameLabel}
                                        </button>
                                    ) : (
                                        customerNameLabel
                                    )}
                                </h2>
                            </div>
                            <p className="text-xs text-gray-500">{maskedDisplayPhone ?? "--"}</p>
                            {showExistingMemberLookup && isUnknownCustomer && (
                                <div className="mt-2">
                                    <div className="flex items-center gap-2">
                                        <Input
                                            register={formHook.register("lookup_mobile", {
                                                required: true,
                                            })}
                                            errorMessages={[{ type: "required", message: "Mobile number is required" }]}
                                            className="h-[34px] w-[200px] flex-1 text-xs rounded-md border-[rgba(226,226,226,1)]"
                                            fieldError={formHook.formState.errors.lookup_mobile}
                                            placeholder="Enter mobile number"
                                            value={lookupMobile}
                                            onChange={(event) => {
                                                const cleanedInput  = event?.target?.value?.replace(/\D/g, "").slice(0, 10);
                                                setLookupMobile(cleanedInput);
                                                formHook.clearErrors("lookup_mobile");
                                                if (lookupError) setLookupError("");
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleLookupExistingCustomer}
                                            className="h-[34px] rounded-[6px] bg-black px-3 text-xs font-medium text-white truncate"
                                        >
                                            Search Customer
                                        </button>
                                    </div>
                                    {lookupError && <p className="mt-1 text-[10px] text-red-500">{lookupError}</p>}
                                    {matchedCustomer && (
                                        <div className="mt-2 rounded-[6px] bg-white p-2">
                                            <p className="text-xs font-medium text-gray-900">
                                                {matchedCustomer.customer_details.name}
                                            </p>
                                            <p className="text-[10px] text-gray-600">
                                                {maskMobile(matchedCustomer.customer_details.mobile)}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {isUnknownCustomer ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowExistingMemberLookup((prev) => !prev);
                                        setLookupError("");
                                        if (!lookupMobile) {
                                            setLookupMobile((displayPhone ?? "").replace(/\D/g, "").slice(-10));
                                        }
                                    }}
                                    className="inline-flex h-[20px] items-center rounded-[44px] border border-[rgba(226,226,226,1)] px-[8px] text-[10px] font-medium leading-[14px] text-blue-600 hover:underline"
                                >
                                    Already a User?
                                </button>
                            ) : (
                                !!personaLabel && (
                                    <span className="inline-flex h-[20px] items-center rounded-[44px] border border-[rgba(226,226,226,1)] px-[8px] text-[10px] font-medium leading-[14px] text-gray-600">
                                        {personaLabel}
                                    </span>
                                )
                            )}
                        </div>
                    </div>

                    {/* Pets list */}
                    <div className="pb-[12px]">
                        {pets.length === 0 && (
                            <p className="text-xs text-gray-500 pt-3">No pets found for this customer.</p>
                        )}
                        {visiblePets?.map((pet, index) => {
                            const vaccinationBadge = getVaccinationBadge(pet.vaccination_status);
                            const isVaccinated = vaccinationBadge.variant === "up_to_date";
                            return (
                                <div
                                    key={pet.id}
                                    className={
                                        index === 0
                                            ? "py-3"
                                            : "border-t border-[rgba(226,226,226,1)] pt-[12px] pb-[12px]"
                                    }
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2">
                                            <img
                                                src={pet.species_id === "1" ? dog : cat}
                                                alt="pet"
                                                className="w-4 h-4"
                                            />
                                            <span className="text-xs font-medium">{pet.name ?? "Unnamed Pet"}</span>
                                        </div>
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            {pet.high_risk && (
                                                <span className="rounded-[44px] border-[0.5px] border-[#FFBDC8] bg-[linear-gradient(111deg,#FFE6EF_29.47%,#FFC7DB_106.2%)] shadow-[inset_0_-2px_4px_rgba(255,149,149,0.25),inset_0_4px_4px_rgba(255,255,255,0.25)] px-[8px] py-[2px] text-[10px] font-medium leading-[14px] text-[rgba(241,105,127,1)] text-center">
                                                    High Care
                                                </span>
                                            )}
                                            <span
                                                className={getVaccinationBadgeClass(vaccinationBadge?.variant)}
                                                onClick={() => {
                                                    const vaccinationUrl = customerIdForProfile
                                                        ? resolvedPetId
                                                            ? `${getRouteKey("VACCINATION_TAB", "url")}/${customerIdForProfile}/${resolvedPetId}`
                                                            : `${getRouteKey("VACCINATION_TAB", "url")}/${customerIdForProfile}`
                                                        : "";
                                                    if (vaccinationUrl) {
                                                        window.open(vaccinationUrl, "_self");
                                                    }
                                                }}
                                            >
                                                <img
                                                    src={isVaccinated ? gvaccinationIcon : rvaccinationIcon}
                                                    alt="vaccination status"
                                                    className="h-[10px] w-[10px]"
                                                />
                                                {vaccinationBadge?.label}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {isUnknownCustomer && (
                            <div className="pt-2">
                                {matchedCustomerProfileUrl && (
                                    <button
                                        type="button"
                                        onClick={() => window.open(matchedCustomerProfileUrl, "_self")}
                                        className="w-full rounded-[6px] border border-[rgba(226,226,226,1)] bg-white px-3 py-2 text-xs font-medium text-blue-600 hover:underline"
                                    >
                                        Open Related Customer Profile
                                    </button>
                                )}
                            </div>
                        )}
                        {hasMorePets && (
                            <button
                                type="button"
                                onClick={() => setShowAllPets((prev) => !prev)}
                                className="mt-[4px] flex w-full items-center justify-center text-xs text-blue-600 underline gap-1"
                            >
                                {showAllPets ? "Show less" : "View all pets"}
                                {showAllPets ? (
                                    <ChevronUp className="h-[16px] w-[16px]" />
                                ) : (
                                    <ChevronDown className="h-[16px] w-[16px]" />
                                )}
                            </button>
                        )}
                    </div>
                    <hr className="bg-gray-200" />
                    {callDetails?.city && (
                        <div className="flex items-center gap-3 py-[8px]">
                            <img src={clinicIcon} alt="clinic" className="h-[16px] w-[16px] shrink-0" />
                            <span className="text-sm font-medium text-gray-900">{callDetails.city}</span>
                        </div>
                    )}

                </div>

                {isUnknownCustomer && (
                    <div className="px-[12px]">
                        <button
                            type="button"
                            onClick={() => setOpenSmartQCDeeplink(true)}
                            title="Generate a Smart QC deeplink for this customer"
                            className="w-full inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-violet-200 bg-violet-50 px-3 text-theme-xs font-semibold text-violet-800 shadow-sm transition-colors hover:bg-violet-100 hover:border-violet-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-violet-300 my-1"
                        >
                            <HiOutlineLink className="h-4 w-4 shrink-0" />
                            QC Deeplink
                        </button>
                    </div>
                )}

                <ExotelTalkingPoints
                    resolvedCustomerId={resolvedCustomerId}
                    customerIdForProfile={customerIdForProfile}
                    resolvedPetId={resolvedPetId}
                    displayPhone={displayPhone}
                    resolvedActiveCRT={resolvedActiveCRT}
                    agentEmail={authUser?.email ?? ""}
                    resolvedDidNumber={resolvedDidNumber}
                />

                {openSmartQCDeeplink && (
                    <SmartQCDeeplinkModal
                        open={openSmartQCDeeplink}
                        setOpen={setOpenSmartQCDeeplink}
                        customerId={customerIdForProfile}
                        customerMobile={displayPhone}
                        customerName={callDetails?.customer?.name}
                    />
                )}

                {/* ── Call info + Disposition ── */}
                <div className="space-y-3 rounded-xl bg-[rgba(249,249,248,1)] mt-2 pt-[12px] pl-[12px] pr-[12px] pb-[12px]">
                    {/* Call meta row */}
                    <div className="flex items-center justify-between gap-[10px] w-full">
                        <div className="flex items-center gap-[10px] min-w-0 flex-1">
                            <span className="text-xs flex items-center gap-2 shrink-0">
                                <img src={phoneicon} alt="phone call" className="h-[14px] w-[14px] shrink-0" />
                                <span className="font-semibold text-black whitespace-nowrap">{resolvedDirectionLabel}</span>
                            </span>
                            <span className="text-[#A3A3A3] font-medium text-xs">
                                {callDetails?.queue_name || "--"}
                            </span>
                        </div>

                        <div className="flex items-center gap-[8px] shrink-0">
                            {initiatedAtLabel && (
                                <span className="text-[#A3A3A3] font-medium text-xs whitespace-nowrap">
                                    {initiatedAtLabel}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Disposition dropdowns */}
                    <div className="space-y-[8px]">
                        <SearchableSelectMenu
                            errorMessages={[{ message: "L1 disposition is required", type: "required" }]}
                            onSelectItem={(item) => {
                                formHook.setValue("disposition_l1_code", item?.id ?? "", { shouldValidate: true });
                                formHook.setValue("disposition_l2_code", "", { shouldValidate: true });
                            }}
                            fieldError={formHook.formState.errors.disposition_l1_code}
                            register={formHook.register("disposition_l1_code", { required: true })}
                            selectItems={dispositionL1Data}
                            placeholder="L1 Disposition"
                            showTooltips
                            showTypedErrors={false}
                            showDropdownIcon
                            defaultSelected={selectedL1Item}
                            listBoxClassName="w-full"
                            className="text-[rgba(108,114,127,1)] border-[rgba(226,226,226,1)] bg-[rgba(255,255,255,1)]"
                            containerClassName="w-full"
                        />

                        <SearchableSelectMenu
                            errorMessages={[{ message: "L2 disposition is required", type: "required" }]}
                            onSelectItem={(item) => {
                                formHook.setValue("disposition_l2_code", item?.id ?? "", { shouldValidate: true });
                            }}
                            fieldError={formHook.formState.errors.disposition_l2_code}
                            register={formHook.register("disposition_l2_code", { required: true })}
                            selectItems={dispositionL2Data}
                            placeholder={selectedL1Code ? "L2 Dispositions" : "Select L1 first"}
                            showTooltips
                            showTypedErrors={false}
                            showDropdownIcon
                            defaultSelected={selectedL2Item}
                            disabled={!selectedL1Code}
                            listBoxClassName="w-full"
                            className="text-[rgba(108,114,127,1)] border-[rgba(226,226,226,1)] bg-[rgba(255,255,255,1)] disabled:bg-[rgba(238,238,238,1)] disabled:text-[rgba(108,114,127,1)]"
                            containerClassName="w-full"
                        />

                        <textarea
                            rows={1}
                            placeholder="Comments"
                            {...formHook.register("description")}
                            className="block h-[40px] w-full resize-none rounded-[6px] border border-[rgba(226,226,226,1)] bg-[rgba(255,255,255,1)] px-[10px] py-[8px] text-xs text-[rgba(108,114,127,1)] placeholder:text-[rgba(156,163,175,1)] focus:outline-none"
                        />
                    </div>

                    {/* Dispose button */}
                    <button
                        disabled={isDisposeDisabled}
                        onClick={handleDisposeCall}
                        className="cursor-pointer w-full rounded-[6px] bg-[#000] p-[10px] text-xs font-medium text-[#fff] disabled:bg-[rgba(238,238,238,1)] disabled:text-[rgba(108,114,127,1)]"
                    >
                        {isDisposingCall ? "Syncing..." : "Dispose Call"}
                    </button>
                </div>
            </div>

            {isPopupLoading && (
                <p className="text-xs text-gray-500 px-[12px] py-[8px]">Loading call details...</p>
            )}
        </div>
    );
};

export default VeticExotelIntegration;
